/*
 * Пилюля брони (docs/reference/phase-3-mockup.md §6). Только вид: раскладку (grid-column/
 * grid-row) и клик по данным ставит полотно снаружи (задача 4) через className/style/onClick —
 * здесь их нет ни в разметке, ни в CSS.
 *
 * `<button>`, а не `<div onClick>` макета: тот же выбор, что уже сделан для строки таблицы броней
 * (widgets/bookings-table/ui/BookingsTable.module.css `.row`) — фокус по Tab и активация по
 * Enter/Space достаются от браузера бесплатно, без ручного обработчика клавиш.
 */
.root {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding: 0 7px;
  margin: 5px 3px;
  z-index: 2;
  border: 0;
  border-radius: 5px;
  overflow: hidden;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;
  appearance: none;
  /* Фолбэк незнакомого статуса — `--tint` приезжает инлайном из `statusToken()`. Ниже её
     перебивают точные `[data-status]`-правила (специфичность 0,2,0 против 0,1,0 здесь, так
     что порядок объявления не важен — атрибут-селектор побеждает всегда).
     ⚠️ Числа правил здесь не держим. Оно уже устарело молча: правила были написаны на `in`/`out`,
     а словарь статусов свели с доменом — селекторы перестали совпадать с чем-либо, и обе плитки
     тихо уехали в этот фолбэк. Мёртвого селектора не видят ни tsc, ни линт, ни тесты. */
  background: color-mix(in oklch, var(--tint) 20%, var(--tint-on));
  box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--tint) 34%, transparent);
  /*
   * Полусуточная модель заезда/выезда. Бронь идёт от СЕРЕДИНЫ дня заезда до СЕРЕДИНЫ дня выезда:
   * сетка держит пилюлю в полу-открытом интервале дневных колонок (ширина = число ночей), а этот
   * сдвиг вправо на пол-колонки (`--dw`/2) ставит левый край на полдень заезда, правый — на полдень
   * выезда. Ширина не меняется, только положение → смежные брони одного объекта делят день оборота:
   * одна кончается в полдень, следующая с того же полудня начинается, а штатные горизонтальные
   * margin'ы пилюль дают аккуратный зазор-шов ровно на полуденной направляющей.
   *
   * `--pill-shift` ставит полотно инлайном (`calc(var(--dw)/2)`); дефолт `0px` — пилюля без полотна
   * не сдвинута. СОСТАВЛЯЕТСЯ с ховер-подъёмом ниже: `transform` — одно свойство и сам не
   * складывается, поэтому `translateY(-1px)` на ховере обязан ПОВТОРИТЬ сдвиг, иначе пилюля
   * прыгала бы влево при наведении.
   */
  transform: translateX(var(--pill-shift, 0px));
}

.root[data-status='checked_in'] {
  background: color-mix(in oklch, var(--st-in) 22%, var(--tint-on));
  box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--st-in) 36%, transparent);
}

.root[data-status='confirmed'] {
  background: color-mix(in oklch, var(--st-confirmed) 20%, var(--tint-on));
  box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--st-confirmed) 32%, transparent);
}

.root[data-status='pending'] {
  background: color-mix(in oklch, var(--st-pending) 24%, var(--tint-on));
  box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--st-pending) 36%, transparent);
}

.root[data-status='cancelled'] {
  background: color-mix(in oklch, var(--st-cancel) 18%, var(--tint-on));
  box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--st-cancel) 36%, transparent);
}

/* Выехал — единственный статус, что красится СПЛОШНЫМ `--sunk`, без color-mix (макет §6). */
.root[data-status='checked_out'] {
  background: var(--sunk);
  box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--st-out) 30%, transparent);
}

/*
 * ⚠️ ЗАЛИВКИ ПИЛЮЛЬ РАЗЛИЧАЮТСЯ СЛАБО, И ЭТО НЕ ПРОМАХ ЭТИХ ДВУХ ПРАВИЛ, А УСТРОЙСТВО ПОЛОТНА.
 * Замерено по вычисленным стилям (расстояние в OKLab между фактическими фонами, светлая тема):
 *
 *     cancelled ↔ pending   0.0055   ← самая близкая пара, и она БЫЛА ЗДЕСЬ ДО НАС
 *     no_show   ↔ pending   0.0126
 *     no_show   ↔ cancelled 0.0126
 *     no_show   ↔ blocked   0.0129
 *
 * То есть тона, смешанные на 16–24% со светлой подложкой, сходятся все; статусы на полотне
 * разводит НЕ заливка, а метки: отменённая зачёркнута, ожидающая несёт «ожид», у выехавшей плоский
 * `--sunk` без цветности, а у блокировки на месте имени стоит ПРИЧИНА. Оба новых правила ложатся
 * в этот же порядок, и ни одно из них не ближе к соседу, чем предсуществующая пара.
 *
 * Гейт контраста этого не поймал бы: он меряет чернила НА заливке и про «заливка против заливки»
 * не спрашивает вовсе. Поймал замер вычисленных стилей на живом полотне.
 */

/* Не приехал — тон свой, а насыщенность как у отменённой: оба исхода «проживания не было». */
.root[data-status='no_show'] {
  background: color-mix(in oklch, var(--st-noshow) 18%, var(--tint-on));
  box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--st-noshow) 36%, transparent);
}

/* Заблокирована — не бронь: заливка глуше гостевых, чтобы полотно читалось «здесь занято, но
   продавать нечего», а рамка заметнее — период всё-таки закрыт. */
.root[data-status='blocked'] {
  background: color-mix(in oklch, var(--st-blocked) 16%, var(--tint-on));
  box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--st-blocked) 38%, transparent);
}

/*
 * Раскраска по ПЛОЩАДКЕ — включается атрибутом `data-cb="platform"` на предке (`#chess-grid`,
 * ставит полотно). `!important` — из макета: там статус-цвет лежит инлайн-стилем, который иначе
 * бьёт любое внешнее правило, и перебить его может только он.
 *
 * ⚠️ ПРАВИЛ НА КОНКРЕТНЫЕ ПЛОЩАДКИ (и токенов `--pf-*` в палитре) БЫТЬ НЕ ДОЛЖНО — это тот же
 * приватный словарь, что у бейджа, только этажом ниже: пятая площадка не получит цвета вовсе, а
 * справочник домена растёт сам, незнакомая заводится автоматически.
 *
 * Правило ОДНО и читает `--pf` — цвет бренда, пришедший с сервера вместе с бронью. Доля
 * подмешивания та же, что была у площадок макета, и по той же причине, что у бейджа: светлота
 * результата ограничена подложкой при любом входе, поэтому имя гостя поверх читается независимо
 * от того, что прислал сервер.
 *
 * ⚠️ СОЗНАТЕЛЬНОЕ РАСХОЖДЕНИЕ С МАКЕТОМ. Там `direct`/`realtycalendar`/`manual`/`import` правил не
 * имели и в режиме площадок оставались на статус-цвете — не по замыслу, а потому что цвета для них
 * не было. Теперь цвет есть у КАЖДОГО происхождения, и решает его сервер: держать исключения
 * списком у себя значило бы вернуть словарь, который эта правка и сносит.
 */
:global([data-cb='platform']) .root[data-pf] {
  background: color-mix(in oklch, var(--pf) 20%, var(--tint-on)) !important;
  box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--pf) 38%, transparent) !important;
}

.dot {
  width: 5px;
  height: 5px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--st-in);
}

.name {
  overflow: hidden;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  text-overflow: ellipsis;
}

/*
 * `--ink-2`, НЕ `--ink-3` макета: замерено `tools/contrast.mjs` перед реализацией — на заливках
 * ЭТОЙ пилюли (18–24%, а не 9–16% у чипов статуса/событий, где `--ink-3` уже проверен) `--ink-3`
 * не берёт AA сразу в нескольких сочетаниях статус/площадка (худшее — 4.03 при норме 4.5, светлая
 * тема, заливка «заехал»). `--ink-2` держит запас на ВСЕХ статусах и площадках в обеих темах
 * (худшее — 6.74). Тот же принцип, что уже применён к бейджу roadmap и пилюлям платежей
 * (contrast.mjs, «ЦЕНА ПРАВИЛА 2»): приглушённый текст на цветной заливке не может быть САМЫМ
 * тусклым тоном шкалы.
 */
.name.dim {
  color: var(--ink-2);
}

.name.strike {
  text-decoration: line-through;
}

.sum {
  flex-shrink: 0;
  margin-left: auto;
  font-family: 'JetBrains Mono Variable', monospace;
  font-size: 10px;
  /* --ink-2, не --ink-3 макета — то же измерение и та же причина, что у `.name.dim` выше. */
  color: var(--ink-2);
}

/*
 * «ожид» — `--ink`, НЕ цветной `var(--st-pending)` макета: тот же цвет текста на заливке ИЗ ТОГО
 * ЖЕ тона даёт 2.40–2.63 : 1 в светлой теме (contrast.mjs, замерено перед реализацией) — ровно тот
 * разворот, что уже случился с чипом статуса, бейджем roadmap и пилюлями платежей («Правило 2» в
 * contrast.mjs: цвет остаётся на заливке/рамке, текст переезжает на `--ink`).
 */
.pending {
  flex-shrink: 0;
  margin-left: auto;
  font-family: 'JetBrains Mono Variable', monospace;
  font-size: 9px;
  color: var(--ink);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/*
 * Hover — макет §6: `translateY(-1px)` + тень, которая ПОЛНОСТЬЮ заменяет box-shadow (а не
 * добавляется к рамке статуса — так и в макете, там это один `style-hover`, переписывающий то же
 * CSS-свойство). Правило объявлено ПОСЛЕДНИМ: специфичность у `:hover` та же, что у `[data-status]`
 * (класс+псевдокласс против класс+атрибут — оба 0,2,0), выигрывает порядок в файле. Правила
 * площадки (`!important`, 0,3,0) всё равно выигрывают ВСЕГДА независимо от порядка — наведение на
 * закрашенную по площадке пилюлю не меняет рамку, поднимается только сама плашка, ровно как и в
 * макете (там статус-цвет — инлайн-стиль, который бьёт любой хвер и уступает только `!important`).
 */
.root:hover {
  transform: translateX(var(--pill-shift, 0px)) translateY(-1px);
  box-shadow: var(--shadow-hover);
}
